import React, { useEffect, useRef } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';

import { IDeviceAnomalyEventDataPoint } from '../../../../../services/core/deviceAnomaliesCharts';
import { StackedColumnsDateChart } from '../../../../../components/chart/stackedColumnsDateChart';
import { drawXAxisLabelsFromDateRange } from '../../../../../components/chart/lib';
import { formatTooltipDay } from '../../../../../components/chart/tooltips';
import { useTranslation } from '@lib/useTypedTranslation';
import { getAnomalyBarClickHandler } from './lib';

const CHART_DAYS = 30;

// Colour palette: grey = training, blue = recent, red = anomaly
const TRAINING_COLOUR = '#9e9e9e';
const RECENT_COLOUR = '#4b6a88';
const ANOMALY_COLOUR = '#c0392b';

// The selected day's own bar is faded to mark it, instead of overlaying a separate highlight band
// across the chart (design feedback: the band read as too visually heavy on its own). Every other
// bar keeps its normal, fully-opaque colour.
export const SELECTED_COLUMN_OPACITY = 0.35;
export const DEFAULT_COLUMN_OPACITY = 1;

interface ITransformedDataPoint {
  date: number,
  trainingCount: number | null,
  recentCount: number | null,
  anomalyCount: number | null
}

function transformEventData(data: IDeviceAnomalyEventDataPoint[]): ITransformedDataPoint[] {
  return data.map((point) => ({
    date: Number(point.date),
    trainingCount: point.period === 'training' ? point.count : null,
    recentCount: point.period === 'evaluation' && !point.isAnomaly ? point.count : null,
    anomalyCount: point.period === 'evaluation' && point.isAnomaly ? point.count : null
  }));
}

// Finds whichever series' column matches the given date - exactly one series is ever populated
// for a given day (see transformEventData), so at most one column will ever match.
function findColumnForDate(chart: am4charts.XYChart, date: number): am4charts.Column | undefined {
  let found: am4charts.Column | undefined;
  chart.series.each((series) => {
    if (found) {
      return;
    }
    (series as am4charts.ColumnSeries).columns.each((column) => {
      const dataContext = column.dataItem?.dataContext as ITransformedDataPoint | undefined;
      if (!found && dataContext && Number(dataContext.date) === date) {
        found = column;
      }
    });
  });
  return found;
}

// Sets fillOpacity via the column's own defaultState (rather than just assigning the live
// property) and re-applies it with setState('default'). addHoverAnimation (components/chart/
// onHoverAnimation.ts, wired up for every bar chart in stackedColumnsChart.tsx) calls
// column.setState('default') on every column in the chart whenever the mouse leaves any bar, to
// clear its own hover-fade effect - that call re-applies the column's defaultState.properties,
// which would silently overwrite a plain fillOpacity assignment the next time anyone hovers away
// from anything. Updating defaultState.properties instead means that same reset re-applies our
// value rather than undoing it.
function setColumnOpacity(column: am4charts.Column, opacity: number) {
  column.defaultState.properties.fillOpacity = opacity;
  column.setState('default');
}

// Marks the currently examined day by fading its own bar, clearing any previous
// selection's opacity first, so it's obvious which non-zero column the device information and
// section below are currently showing. Exported (rather than left inline in the useEffect below)
// so its "chart not ready yet" guard can be exercised directly by a unit test: React always
// flushes drawXAxis (a layout-timed componentDidMount) for every chart in the tree before a
// passive useEffect ever runs, so chartRef.current is always set by the time the real effect runs
// in practice, making the guard unreachable through an actual render.
export function applySelectedDateHighlight(
  chart: am4charts.XYChart | null,
  highlightedColumnRef: React.MutableRefObject<am4charts.Column>,
  selectedDate: number | undefined
) {
  if (!chart) {
    return;
  }
  if (highlightedColumnRef.current) {
    setColumnOpacity(highlightedColumnRef.current, DEFAULT_COLUMN_OPACITY);
    highlightedColumnRef.current = null;
  }
  if (selectedDate) {
    const column = findColumnForDate(chart, selectedDate);
    if (column) {
      setColumnOpacity(column, SELECTED_COLUMN_OPACITY);
      highlightedColumnRef.current = column;
    }
  }
}

interface IProps {
  data: IDeviceAnomalyEventDataPoint[],
  title: string,
  cypressId: string,
  timeZone: string,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  /** Called with the clicked day's timestamp when any non-zero column is clicked. */
  onDayClick?: (date: number) => void,
  /** Fades the given day's bar on the chart - the day currently under examination. */
  selectedDate?: number
}

export function DeviceAnomaliesEventChart(props: IProps) {
  const { t } = useTranslation('anomalyDetectionBeta');
  const { t: tBase } = useTranslation('translation');
  const transformed = transformEventData(props.data);
  const dateRange = transformed.length ? {
    from: Math.min(...transformed.map((point) => point.date)),
    to: Math.max(...transformed.map((point) => point.date))
  } : undefined;
  // Clicking any non-zero column (training/recent/anomaly) - not just an anomaly one - switches
  // the examined date; exactly one of these is ever populated for a given day (see
  // transformEventData), so this is really just "is this day's count non-zero".
  const onBarClick = getAnomalyBarClickHandler<ITransformedDataPoint>(
    (dataPoint) => Boolean(dataPoint.trainingCount || dataPoint.recentCount || dataPoint.anomalyCount),
    props.onDayClick
  );

  // drawXAxis (below) only runs once, at amCharts' initial mount - it never re-runs just because
  // selectedDate changes (the chart's data hasn't changed), so the highlight couldn't otherwise
  // move to the newly clicked day. Repainting it here instead, against the same long-lived chart
  // instance drawXAxis captures, also keeps the chart itself from ever remounting - remounting
  // would recreate its Y-axis and drop it out of sync with the battery chart below it, which
  // amCharts axis-width alignment (useAlignCharts) can only keep aligned across a stable set of
  // chart instances.
  const chartRef = useRef<am4charts.XYChart>(null);
  const highlightedColumnRef = useRef<am4charts.Column>(null);

  useEffect(() => {
    const chart = chartRef.current;
    applySelectedDateHighlight(chart, highlightedColumnRef, props.selectedDate);
    // amCharts creates each series' actual column elements asynchronously, as part of its own
    // data validation cycle - on the very first run right after mount, that cycle may not have
    // completed yet, so the call above can find no column to fade. Re-applying once validation
    // completes catches that case; on later selectedDate changes the columns already exist, so
    // this simply repeats the same, already-correct result.
    //
    // Both the chart's own 'validated' AND each series' own 'validated' are listened to (rather
    // than just the chart's, which AmChart itself also waits on before marking the chart "ready"):
    // the chart can dispatch its own 'validated' once its own layout pass completes, before every
    // series has necessarily finished creating its columns - confirmed under heavy concurrent load
    // (many charts mounting at once, e.g. a full parallel test run), where a series' columns can
    // still be getting created well after the chart itself - and hence the visible "chart-ready"
    // state - already reports ready. Each series dispatches its own 'validated' once ITS columns
    // exist, so listening there too closes that gap without depending on exact amCharts timing.
    const disposer = chart?.events.on('validated', () => {
      applySelectedDateHighlight(chart, highlightedColumnRef, props.selectedDate);
    });
    const seriesDisposers: { dispose: () => void }[] = [];
    chart?.series.each((series) => {
      seriesDisposers.push(series.events.on('validated', () => {
        applySelectedDateHighlight(chart, highlightedColumnRef, props.selectedDate);
      }));
    });
    return () => {
      disposer?.dispose();
      seriesDisposers.forEach((seriesDisposer) => seriesDisposer.dispose());
    };
  }, [props.selectedDate]);

  return (
    <StackedColumnsDateChart<any>
      data={transformed}
      series={[
        {
          visible: true,
          dataKey: 'trainingCount',
          description: t('TRAINING'),
          colour: TRAINING_COLOUR
        },
        {
          visible: true,
          dataKey: 'recentCount',
          description: t('RECENT'),
          colour: RECENT_COLOUR
        },
        {
          visible: true,
          dataKey: 'anomalyCount',
          description: t('ANOMALY_PERIOD'),
          colour: ANOMALY_COLOUR
        }
      ]}
      title={props.title}
      days={CHART_DAYS}
      cypressId={props.cypressId}
      hasLegend={true}
      xAxisType={'date'}
      onBarClick={onBarClick}
      drawXAxis={(chart, xAxis) => {
        chartRef.current = chart;
        const dateAxis = xAxis as am4charts.DateAxis;
        if (dateRange) {
          drawXAxisLabelsFromDateRange(dateAxis, dateRange);
        }
        xAxis.title.text = `[font-style: italic]${tBase('TIMEZONE')} (${props.timeZone})[/]`;
      }}
      formatTooltipDate={formatTooltipDay}
      onMount={props.onMount}
      useAxisTooltip
      hideNullSeriesInTooltip
    />
  );
}
